import { useEffect, useState } from 'react';
import {
  CONTROL_ASSESSMENTS,
  CONTROL_LABELS,
  isFiscal,
  type ControlAssessment,
  type Entity,
  type Purpose,
} from '@ave/shared';
import { api } from '../lib/api';
import { Icon } from './icons';

/**
 * Recording a holding, and the entity it is in.
 *
 * Stage 0 will not close without a holding, and until this existed the only
 * way to record one was to call the API by hand — so a new engagement could
 * be created through the UI and then not be advanced through it.
 *
 * Two things the form does rather than merely collects.
 *
 * **It creates the entity in the same step where that is what is missing.**
 * A first holding needs a company to be a holding in, and sending someone to
 * a different screen to make one is how a form gets abandoned halfway.
 *
 * **It asks the s.161 question for a fiscal engagement rather than leaving
 * it blank.** Related property is a blocking check at this stage, and it is
 * the sort of question that is easy to leave for later and expensive to
 * answer at the review gate. "None" is a perfectly good answer; not having
 * been asked is not.
 */
export function RecordHolding({
  engagementId,
  purpose,
  entities,
  onSaved,
  openSignal = 0,
}: {
  engagementId: string;
  purpose: Purpose;
  entities: Entity[];
  onSaved: () => void | Promise<void>;
  /**
   * Increments when a blocking check sends someone here. A counter rather
   * than a boolean: the form can be closed again and re-opened by a second
   * click on the same check, which a boolean would swallow.
   */
  openSignal?: number;
}) {
  const fiscal = isFiscal(purpose);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entityId, setEntityId] = useState<string>(entities[0]?.id ?? 'new');
  const [entityName, setEntityName] = useState('');
  const [companiesHouseNo, setCompaniesHouseNo] = useState('');

  const [holderDescription, setHolderDescription] = useState('');
  const [shareClass, setShareClass] = useState('ordinary');
  const [sharesHeld, setSharesHeld] = useState('');
  const [sharesInIssue, setSharesInIssue] = useState('');
  const [controlAssessment, setControlAssessment] = useState<ControlAssessment>('small_minority');
  const [relatedPropertyNotes, setRelatedPropertyNotes] = useState('');
  const [aggregatedPct, setAggregatedPct] = useState('');

  const creatingEntity = entityId === 'new';
  const held = Number(sharesHeld);
  const issued = Number(sharesInIssue);
  // Shown, not stored: the API derives the percentage from the two counts, so
  // a figure typed here would be a third version of the same fact.
  const derivedPct =
    Number.isFinite(held) && Number.isFinite(issued) && issued > 0 && sharesHeld && sharesInIssue
      ? (held / issued) * 100
      : null;

  // The s.161 answer is required for every purpose, not only fiscal ones:
  // the Stage 0 check counts holdings with no answer at all and does not
  // look at the purpose. A form that let the field go blank would produce a
  // holding that blocks its own gate the moment it is saved.
  const valid =
    holderDescription.trim().length > 1 &&
    (creatingEntity ? entityName.trim().length > 1 : true) &&
    relatedPropertyNotes.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const id = creatingEntity
        ? (
            await api.createEntity(engagementId, {
              name: entityName.trim(),
              companiesHouseNo: companiesHouseNo.trim() || undefined,
              entityType: 'company',
            })
          ).entity.id
        : entityId;

      await api.createHolding(engagementId, {
        entityId: id,
        holderDescription: holderDescription.trim(),
        shareClass: shareClass.trim() || 'ordinary',
        sharesHeld: sharesHeld.trim() || undefined,
        sharesInIssue: sharesInIssue.trim() || undefined,
        controlAssessment,
        relatedPropertyNotes: relatedPropertyNotes.trim() || undefined,
        aggregatedPct: aggregatedPct.trim() ? String(Number(aggregatedPct) / 100) : undefined,
      });

      setHolderDescription('');
      setSharesHeld('');
      setSharesInIssue('');
      setRelatedPropertyNotes('');
      setAggregatedPct('');
      setEntityName('');
      setCompaniesHouseNo('');
      setOpen(false);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the holding');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="border-t border-slate-100 px-6 py-3">
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
          Record a holding
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-6 py-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="label">Company the shares are in</span>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
                {entity.companiesHouseNo ? ` (${entity.companiesHouseNo})` : ''}
              </option>
            ))}
            <option value="new">A company not yet recorded…</option>
          </select>
        </label>

        {creatingEntity ? (
          <>
            <label className="block">
              <span className="label">Company name</span>
              <input
                autoFocus
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                placeholder="e.g. Marchpole Estates Limited"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
            <label className="block">
              <span className="label">Companies House number</span>
              <input
                value={companiesHouseNo}
                onChange={(e) => setCompaniesHouseNo(e.target.value)}
                placeholder="Optional, but it is what the register is pulled on"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </>
        ) : null}

        <label className="block sm:col-span-2">
          <span className="label">Whose holding this is</span>
          <input
            value={holderDescription}
            onChange={(e) => setHolderDescription(e.target.value)}
            placeholder="e.g. The deceased, Mrs E. Warrender"
            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </label>

        <label className="block">
          <span className="label">Share class</span>
          <input
            value={shareClass}
            onChange={(e) => setShareClass(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </label>

        <label className="block">
          <span className="label">Control assessment</span>
          <select
            value={controlAssessment}
            onChange={(e) => setControlAssessment(e.target.value as ControlAssessment)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            {CONTROL_ASSESSMENTS.map((c) => (
              <option key={c} value={c}>
                {CONTROL_LABELS[c]}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="label">Shares held</span>
          <input
            inputMode="decimal"
            value={sharesHeld}
            onChange={(e) => setSharesHeld(e.target.value)}
            placeholder="3000"
            className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </label>

        <label className="block">
          <span className="label">Shares in issue</span>
          <input
            inputMode="decimal"
            value={sharesInIssue}
            onChange={(e) => setSharesInIssue(e.target.value)}
            placeholder="10000"
            className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <span className="mt-1 block text-xs text-slate-400">
            {derivedPct === null
              ? 'The percentage is derived from these two, never typed.'
              : `A holding of ${derivedPct.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%.`}
          </span>
        </label>

        <label className="block sm:col-span-2">
          <span className="label">
            Related property — IHTA 1984 s.161
            <span className="ml-1.5 font-normal normal-case tracking-normal text-amber-700">
              {fiscal ? 'required; it aggregates the holding' : 'required, even here'}
            </span>
          </span>
          <textarea
            value={relatedPropertyNotes}
            onChange={(e) => setRelatedPropertyNotes(e.target.value)}
            rows={2}
            placeholder="e.g. None — the spouse holds no shares in this company. Or: 2,000 ordinary held by the surviving spouse, aggregated under s.161."
            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <span className="mt-1 block text-xs text-slate-400">
            {fiscal
              ? 'A spouse’s holding is aggregated with the deceased’s for valuation, and the discounts are then taken on the aggregate. “None” is an answer; silence is not.'
              : 'Asked on every engagement, because a holding recorded without an answer blocks the gate. “Not applicable — commercial sale” is a complete answer.'}
          </span>
        </label>

        <label className="block">
          <span className="label">Aggregated holding, if any (%)</span>
          <input
            inputMode="decimal"
            value={aggregatedPct}
            onChange={(e) => setAggregatedPct(e.target.value)}
            placeholder="e.g. 50"
            className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <span className="mt-1 block text-xs text-slate-400">
            The s.161 aggregate the discounts are computed on.
          </span>
        </label>
      </div>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Recording…' : 'Record the holding'}
        </button>
      </div>
    </div>
  );
}
